import forEach from "lodash/forEach";
import { PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";

import css from "./filters_v2_container_with_show_more.module.scss";
import { FiltersV2PreviewMoreButton } from "./parts/filters_v2_show_more_button";

type FiltersV2ContainerWithShowMoreProps = PropsWithChildren<{
  disabled?: boolean;
}>;

export const FiltersV2ContainerWithShowMore = ({
  children,
  disabled
}: FiltersV2ContainerWithShowMoreProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const showMoreButtonRef = useRef<HTMLDivElement | null>(null);
  const [hiddenItemsCount, setHiddenItemsCount] = useState(0);
  const [maxWidth, setMaxWidth] = useState(0);
  const [height, setHeight] = useState(0);

  const calculateVisibleItems = useCallback(() => {
    if (!contentRef.current || !containerRef.current) {
      return;
    }

    const availableWidth = containerRef.current.clientWidth;

    const offsetX = contentRef.current.getBoundingClientRect().left;

    setHeight(contentRef.current.offsetHeight);
    setHiddenItemsCount(contentRef.current!.children.length);
    setMaxWidth(0);

    forEach(contentRef.current.children, (child, index) => {
      const buttonWidth =
        index === contentRef.current!.children.length - 1 ? 0 : showMoreButtonRef.current?.clientWidth || 0;

      const currentWidth = child.getBoundingClientRect().right - offsetX;

      if (currentWidth + buttonWidth > availableWidth) {
        return false;
      }

      setHiddenItemsCount(contentRef.current!.children.length - index - 1);
      setMaxWidth(currentWidth);
    });
  }, []);

  useEffect(() => {
    if (!contentRef.current || !containerRef.current) {
      return;
    }

    const containerResizeObserver = new ResizeObserver(calculateVisibleItems);
    const contentResizeObserver = new ResizeObserver(calculateVisibleItems);

    containerResizeObserver.observe(containerRef.current!);
    contentResizeObserver.observe(contentRef.current!);

    return () => {
      containerResizeObserver.disconnect();
      contentResizeObserver.disconnect();
    };
  }, [calculateVisibleItems]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(calculateVisibleItems, [hiddenItemsCount]);

  return (
    <div className={css.relativeContainer} style={{ height }} ref={containerRef}>
      <div className={css.absoluteContainer}>
        <div className={css.constantWidthContainer} style={{ maxWidth }}>
          <div className={css.variableWidthContainer} ref={contentRef}>
            {children}
          </div>
        </div>
        {!disabled && <FiltersV2PreviewMoreButton ref={showMoreButtonRef} count={hiddenItemsCount} />}
      </div>
    </div>
  );
};
